import React, { useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import type { DocNode } from '@atlaskit/adf-schema';

import { TextField } from '../work-create-form-common/TextField';
import { RichTextField } from '../work-create-form-common/RichTextField';
import { messages } from '../../messages';

export interface SingleWorkCreateFormAiSupportedSectionProps {
	initialSummary: string;
	initialDescription: DocNode | undefined;
	handleSummaryChange?: (value: string) => void;
	handleDescriptionChange?: (value: DocNode | undefined) => void;
	isSubmitting?: boolean;
	isCreating?: boolean;
	isDescriptionRequired?: boolean;
	isAiLoading?: boolean;
	isCreatedWithAi?: boolean;
}

export const SingleWorkCreateFormAiSupportedSection: React.FC<
	SingleWorkCreateFormAiSupportedSectionProps
> = ({
	initialSummary,
	initialDescription,
	handleSummaryChange,
	handleDescriptionChange,
	isSubmitting = false,
	isCreating = false,
	isDescriptionRequired = false,
	isAiLoading = false,
	isCreatedWithAi = false,
}) => {
	const { formatMessage } = useIntl();
	const [manualSummary, setManualSummary] = useState<string>(initialSummary);
	const [manualDescription, setManualDescription] = useState<DocNode | undefined>(
		initialDescription,
	);

	const [aiSummary, setAiSummary] = useState<string>(initialSummary);
	const [aiDescription, setAiDescription] = useState<DocNode | undefined>(initialDescription);

	// Update local state when initial values change
	useEffect(() => {
		if (!isAiLoading) {
			if (isCreatedWithAi) {
				setAiSummary(initialSummary);
				setAiDescription(initialDescription);
			} else {
				setManualSummary(initialSummary);
				setManualDescription(initialDescription);
			}
			handleSummaryChange?.(initialSummary);
			handleDescriptionChange?.(initialDescription);
		}
	}, [
		initialSummary,
		initialDescription,
		isAiLoading,
		isCreatedWithAi,
		handleSummaryChange,
		handleDescriptionChange,
	]);

	const onManualSummaryChange = useCallback(
		(value: string) => {
			setManualSummary(value);
			handleSummaryChange?.(value);
		},
		[handleSummaryChange],
	);

	const onManualDescriptionChange = useCallback(
		(value: DocNode | undefined) => {
			setManualDescription(value);
			handleDescriptionChange?.(value);
		},
		[handleDescriptionChange],
	);

	const onAiSummaryChange = useCallback(
		(value: string) => {
			setAiSummary(value);
			handleSummaryChange?.(value);
		},
		[handleSummaryChange],
	);

	const onAiDescriptionChange = useCallback(
		(value: DocNode | undefined) => {
			setAiDescription(value);
			handleDescriptionChange?.(value);
		},
		[handleDescriptionChange],
	);

	return (
		<>
			<TextField
				label={formatMessage(messages.workCreateCommonSummaryLabel)}
				value={isCreatedWithAi ? aiSummary : manualSummary}
				onChange={isCreatedWithAi ? onAiSummaryChange : onManualSummaryChange}
				isRequired
				isDisabled={isSubmitting || isCreating}
				isLoading={isAiLoading}
			/>
			<RichTextField
				label={formatMessage(messages.workCreateCommonDescriptionLabel)}
				value={isCreatedWithAi ? aiDescription : manualDescription}
				onChange={isCreatedWithAi ? onAiDescriptionChange : onManualDescriptionChange}
				isDisabled={isSubmitting || isCreating}
				isRequired={isDescriptionRequired}
				isLoading={isAiLoading}
			/>
		</>
	);
};
